import { useState, useEffect, useRef } from 'react';
import { validators, validateFields, ValidationErrors } from '../../../utils/formValidation';
import { API_BASE_URL, API_PATHS } from '../../../config/api';

// ----------------------------------------------------------------
// Modal: 送信中 or 結果表示
// ----------------------------------------------------------------
interface SubmitModalProps {
  phase: 'loading' | 'result' | null;
  result: boolean | null;
  message: string | null;
  errorMessage: string | null;
  onClose: () => void;
}

const SubmitModal = ({ phase, result, message, errorMessage, onClose }: SubmitModalProps) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (phase !== 'loading') {
      setProgress(0);
      return;
    }
    setProgress(0);
    const steps = [10, 25, 40, 55, 68, 78, 85];
    const timers: ReturnType<typeof setTimeout>[] = [];
    steps.forEach((target, i) => {
      timers.push(setTimeout(() => setProgress(target), i * 420));
    });
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  if (!phase) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 bg-white rounded-2xl shadow-xl w-[90%] max-w-sm mx-auto p-8 flex flex-col items-center gap-6">

        {phase === 'loading' && (
          <div className="w-full text-center">
            <p className="text-sm font-semibold text-gray-700 mb-4">送信中...</p>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#89B56B] to-[#5DAA68] rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-[#89B56B] font-semibold mt-2 text-right">{progress}%</p>
          </div>
        )}

        {phase === 'result' && (
          <>
            <div className="w-full flex flex-col gap-2">
              {message && (
                <div className={`w-full px-4 py-3 rounded-lg text-sm text-center leading-relaxed ${result ? 'bg-[#89B56B]/8 text-gray-700' : 'bg-red-50 text-red-700'}`}>
                  {message}
                </div>
              )}
              {!result && errorMessage && (
                <p className="w-full text-sm text-center text-gray-700 leading-relaxed">
                  {errorMessage}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`w-full py-3 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                result
                  ? 'bg-[#89B56B] text-white hover:bg-[#5DAA68]'
                  : 'bg-red-500 text-white hover:bg-red-600'
              }`}
            >
              閉じる
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------
// エントリーフォーム本体
// ----------------------------------------------------------------
const RecruitEntry: React.FC = () => {
  const [formData, setFormData] = useState({
    application_type: '',
    name: '',
    furigana: '',
    tel: '',
    email: '',
    age: '',
    sex: '',
    remarks: '',
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [modalPhase, setModalPhase] = useState<'loading' | 'result' | null>(null);
  const [apiResult, setApiResult] = useState<boolean | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const buildValidationRules = (data: typeof formData) => [
    { field: 'application_type', value: data.application_type, checks: [(v: string) => validators.required(v, '応募種別')] },
    { field: 'name', value: data.name, checks: [(v: string) => validators.required(v, '氏名')] },
    { field: 'furigana', value: data.furigana, checks: [(v: string) => validators.hiragana(v), (v: string) => validators.optionalMaxLength(v, 20, 'ふりがな')] },
    { field: 'tel', value: data.tel, checks: [(v: string) => validators.phone(v)] },
    { field: 'email', value: data.email, checks: [(v: string) => validators.emailTrimmed(v)] },
    { field: 'age', value: data.age, checks: [(v: string) => validators.age(v)] },
    { field: 'remarks', value: data.remarks, checks: [(v: string) => validators.containsHiragana(v, '備考')] },
  ];

  const validate = () => {
    const newErrors = validateFields(buildValidationRules(formData));
    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // 1. ハニーポット（ダミー項目）のチェック
    const form = e.currentTarget;
    const honeyPotEl = form.elements.namedItem('website') as HTMLInputElement | null;
    const honeyPotVal = honeyPotEl?.value ?? '';
    if (honeyPotVal) {
      // ボット判定：API通信を行わずに成功風モーダルを表示して処理を無効化する
      setApiResult(null);
      setApiMessage(null);
      setApiErrorMessage(null);
      setModalPhase('loading');
      window.setTimeout(() => {
        setApiResult(true);
        setApiMessage('送信が完了しました。担当者より折り返しご連絡いたします。');
        setApiErrorMessage(null);
        setModalPhase('result');
        setFormData({ application_type: '', name: '', furigana: '', tel: '', email: '', age: '', sex: '', remarks: '' });
        setErrors({});
        if (honeyPotEl) honeyPotEl.value = '';
      }, 1000);
      return;
    }

    // 2. 通常の入力値バリデーション
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      const fieldOrder = ['application_type', 'name', 'furigana', 'tel', 'email', 'age', 'remarks'];
      const firstErrorField = fieldOrder.find((f) => newErrors[f]);
      if (firstErrorField && fieldRefs.current[firstErrorField]) {
        fieldRefs.current[firstErrorField]!.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setApiResult(null);
    setApiMessage(null);
    setApiErrorMessage(null);
    setModalPhase('loading');

    try {
      const res = await fetch(`${API_BASE_URL}${API_PATHS.recruitContact}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      const result: boolean = data?.result === true;
      const msg: string | null = typeof data?.message === 'string' ? data.message : (result ? '送信が完了しました。担当者より折り返しご連絡いたします。' : null);
      const errMsg: string | null = !result && typeof data?.errorMessage === 'string' ? data.errorMessage : null;

      setApiResult(result);
      setApiMessage(msg);
      setApiErrorMessage(errMsg);
      setModalPhase('result');

      if (result) {
        setFormData({ application_type: '', name: '', furigana: '', tel: '', email: '', age: '', sex: '', remarks: '' });
        setErrors({});
      }
    } catch {
      setApiResult(false);
      setApiMessage('通信エラーが発生しました。もう一度お試しください。');
      setApiErrorMessage(null);
      setModalPhase('result');
    }
  };

  const handleModalClose = () => {
    setModalPhase(null);
  };

  return (
    <section className="pt-28 pb-16 bg-white relative overflow-hidden" id="entry-form" ref={sectionRef}>
      <div className={`absolute top-8 left-0 text-[80px] sm:text-[180px] font-bold text-[#89B56B]/5 leading-none select-none pointer-events-none font-['Inter'] tracking-tight anim-bg-text ${isVisible ? 'is-visible' : ''}`}>
        Entry
      </div>

      <div className="max-w-3xl mx-auto px-6 lg:px-10 relative">
        <div className={`text-center mb-16 anim-fade-up ${isVisible ? 'is-visible' : ''}`}>
          <p className="text-[#89B56B] text-xs font-semibold tracking-[0.25em] uppercase mb-4 font-['Inter']">
            Entry
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#89B56B] mb-4">エントリーフォーム</h2>
          <p className="text-sm text-gray-500 leading-[1.9]">
            以下のフォームよりご応募いただけます。<br />
            必要事項等を入力し、&ldquo;送信する&rdquo;ボタン押下でご応募完了となります。<br />
            担当者より折り返しご連絡をさせていただきます。
          </p>
        </div>

        <div className={`anim-scale-in ${isVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '0.2s' }}>
          <form
            id="recruit-entry-form"
            noValidate
            onSubmit={handleSubmit}
            className="relative bg-[#F8FAF6] rounded-2xl p-8 md:p-12 shadow-[0_2px_16px_rgba(0,0,0,0.03)]"
          >
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="recruit-website">Website</label>
              <input type="text" name="website" id="recruit-website" tabIndex={-1} autoComplete="off" />
            </div>

            <div className="space-y-6">

              {/* Application Type */}
              <div ref={(el) => { fieldRefs.current['application_type'] = el; }}>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-3">
                  応募種別
                  <span className="text-xs text-white bg-[#89B56B] px-2 py-0.5 rounded">必須</span>
                </label>
                <div className="flex items-center gap-6">
                  {[{ label: '新卒採用', value: '0' }, { label: '中途採用', value: '1' }].map(({ label, value }) => (
                    <label key={value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="application_type"
                        value={value}
                        checked={formData.application_type === value}
                        onChange={handleChange}
                        className="w-4 h-4 text-[#89B56B] accent-[#89B56B]"
                      />
                      <span className="text-sm text-gray-600">{label}</span>
                    </label>
                  ))}
                </div>
                {errors.application_type && (
                  <p className="text-xs text-red-500 mt-1">{errors.application_type}</p>
                )}
              </div>

              {/* Name */}
              <div ref={(el) => { fieldRefs.current['name'] = el; }}>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  氏名
                  <span className="text-xs text-white bg-[#89B56B] px-2 py-0.5 rounded">必須</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={20}
                  className={`w-full px-4 py-3 bg-white border rounded-lg text-sm focus:outline-none focus:border-[#89B56B] focus:ring-1 focus:ring-[#89B56B]/30 transition-colors ${
                    errors.name ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder="姓　名"
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.name ? (
                    <p className="text-xs text-red-500">{errors.name}</p>
                  ) : (
                    <span />
                  )}
                  <p className="text-xs ml-auto text-gray-400">
                    {formData.name.length}/20
                  </p>
                </div>
              </div>

              {/* Furigana */}
              <div ref={(el) => { fieldRefs.current['furigana'] = el; }}>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  ふりがな
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">任意</span>
                </label>
                <input
                  type="text"
                  name="furigana"
                  value={formData.furigana}
                  onChange={handleChange}
                  maxLength={20}
                  className={`w-full px-4 py-3 bg-white border rounded-lg text-sm focus:outline-none focus:border-[#89B56B] focus:ring-1 focus:ring-[#89B56B]/30 transition-colors ${
                    errors.furigana ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder="せい　めい"
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.furigana ? (
                    <p className="text-xs text-red-500">{errors.furigana}</p>
                  ) : (
                    <span />
                  )}
                  <p className="text-xs ml-auto text-gray-400">
                    {formData.furigana.length}/20
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div ref={(el) => { fieldRefs.current['tel'] = el; }}>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  電話番号
                  <span className="text-xs text-white bg-[#89B56B] px-2 py-0.5 rounded">必須</span>
                </label>
                <input
                  type="tel"
                  name="tel"
                  value={formData.tel}
                  onChange={handleChange}
                  maxLength={11}
                  className={`w-full px-4 py-3 bg-white border rounded-lg text-sm focus:outline-none focus:border-[#89B56B] focus:ring-1 focus:ring-[#89B56B]/30 transition-colors ${
                    errors.tel ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder="09012345678"
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.tel ? (
                    <p className="text-xs text-red-500">{errors.tel}</p>
                  ) : (
                    <span />
                  )}
                  <p className="text-xs ml-auto text-gray-400">
                    {formData.tel.length}/11
                  </p>
                </div>
              </div>

              {/* Email */}
              <div ref={(el) => { fieldRefs.current['email'] = el; }}>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  メールアドレス
                  <span className="text-xs text-white bg-[#89B56B] px-2 py-0.5 rounded">必須</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  maxLength={40}
                  className={`w-full px-4 py-3 bg-white border rounded-lg text-sm focus:outline-none focus:border-[#89B56B] focus:ring-1 focus:ring-[#89B56B]/30 transition-colors ${
                    errors.email ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder="example@email.com"
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.email ? (
                    <p className="text-xs text-red-500">{errors.email}</p>
                  ) : (
                    <span />
                  )}
                  <p className="text-xs ml-auto text-gray-400">
                    {formData.email.length}/40
                  </p>
                </div>
              </div>

              {/* Age */}
              <div ref={(el) => { fieldRefs.current['age'] = el; }}>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  年齢
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">任意</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  name="age"
                  value={formData.age}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                    setFormData((prev) => ({ ...prev, age: val }));
                    if (errors.age) {
                      setErrors((prev) => { const next = { ...prev }; delete next.age; return next; });
                    }
                  }}
                  maxLength={2}
                  className={`w-full px-4 py-3 bg-white border rounded-lg text-sm focus:outline-none focus:border-[#89B56B] focus:ring-1 focus:ring-[#89B56B]/30 transition-colors ${
                    errors.age ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder="25"
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.age ? (
                    <p className="text-xs text-red-500">{errors.age}</p>
                  ) : (
                    <span />
                  )}
                  <p className="text-xs ml-auto text-gray-400">
                    {formData.age.length}/2
                  </p>
                </div>
              </div>

              {/* Gender */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-3">
                  性別
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">任意</span>
                </label>
                <div className="flex items-center gap-6">
                  {[{ label: '男性', value: '0' }, { label: '女性', value: '1' }, { label: 'その他', value: '2' }].map(({ label, value }) => (
                    <label key={value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="sex"
                        value={value}
                        checked={formData.sex === value}
                        onChange={handleChange}
                        className="w-4 h-4 text-[#89B56B] accent-[#89B56B]"
                      />
                      <span className="text-sm text-gray-600">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Remarks */}
              <div ref={(el) => { fieldRefs.current['remarks'] = el; }}>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  備考
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">任意</span>
                </label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className={`w-full px-4 py-3 bg-white border rounded-lg text-sm focus:outline-none focus:border-[#89B56B] focus:ring-1 focus:ring-[#89B56B]/30 transition-colors ${
                    errors.remarks ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder="ご質問・ご要望などがあればご記入ください"
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.remarks ? (
                    <p className="text-xs text-red-500">{errors.remarks}</p>
                  ) : (
                    <span />
                  )}
                  <p className="text-xs text-gray-400">
                    {formData.remarks.length}/500
                  </p>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="mt-10 text-center">
              <button
                type="submit"
                disabled={modalPhase === 'loading'}
                className="inline-flex items-center gap-2 px-10 py-3.5 bg-[#89B56B] text-white text-sm font-medium rounded-full hover:bg-[#5DAA68] transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-[#89B56B]/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上記の内容で送信する
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* モーダル */}
      <SubmitModal
        phase={modalPhase}
        result={apiResult}
        message={apiMessage}
        errorMessage={apiErrorMessage}
        onClose={handleModalClose}
      />
    </section>
  );
};

export default RecruitEntry;
